import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import Stack from 'react-bootstrap/Stack';
import Spinner from 'react-bootstrap/Spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookmark } from '@fortawesome/free-solid-svg-icons';
import {
    CategoryWithCountsViewModel,
    CreateBookmarkBindingModel,
    CreateBookmarksBindingModel,
    VideoLibraryApiClient
} from '../apiClient';
import { CategorySelect, CountType } from './inputs/categorySelect';
import { getErrorMessage } from '../helpers/errorHelper';

export type AddBookmarkModalProps = {
    show: boolean;
    onHide: () => void;
    apiClient: VideoLibraryApiClient;
    categories?: CategoryWithCountsViewModel[];
    onSuccess?: () => void;
};

export const AddBookmarkModal: React.FC<AddBookmarkModalProps> = ({
    show,
    onHide,
    apiClient,
    categories,
    onSuccess,
}) => {
    const [url, setUrl] = useState('');
    const [categoryName, setCategoryName] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [availableCategories, setAvailableCategories] = useState<CategoryWithCountsViewModel[]>(categories ?? []);

    useEffect(() => {
        if (categories && categories.length > 0) {
            setAvailableCategories(categories);
        } else if (apiClient?.categoriesAll) {
            apiClient.categoriesAll().then((cats) => {
                if (cats) setAvailableCategories(cats);
            }).catch(console.error);
        }
    }, [apiClient, categories, show]);

    const handleClose = () => {
        if (!submitting) {
            setUrl('');
            setCategoryName('');
            setError(null);
            onHide();
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const trimmedUrl = url.trim();
        if (!trimmedUrl || submitting) return;

        setSubmitting(true);
        setError(null);
        try {
            const result = await apiClient.bookmarksPOST(
                new CreateBookmarksBindingModel({
                    bookmarks: [
                        new CreateBookmarkBindingModel({
                            categoryName: categoryName !== '' ? categoryName : null,
                            url: trimmedUrl,
                        }),
                    ],
                })
            );

            const failedBookmark = result?.failedBookmarks?.[0];
            if (failedBookmark) {
                setError(failedBookmark.reason ?? 'Failed to add bookmark.');
                setSubmitting(false);
                return;
            }

            onSuccess?.();
            setUrl('');
            setCategoryName('');
            onHide();
        } catch (err: any) {
            console.error(err);
            setError(getErrorMessage(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center gap-2">
                    <FontAwesomeIcon icon={faBookmark} className="text-primary" />
                    <span>Add Bookmark</span>
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <Stack gap={3}>
                        {error && (
                            <Alert variant="danger" className="py-2 small mb-0">
                                {error}
                            </Alert>
                        )}
                        <Form.Group controlId="add-bookmark-modal-url">
                            <Form.Label>URL</Form.Label>
                            <Form.Control
                                type="url"
                                placeholder="https://www.youtube.com/watch?v=..."
                                value={url}
                                onChange={(e) => setUrl(e.target.value)}
                                disabled={submitting}
                                autoFocus
                                required
                            />
                        </Form.Group>
                        <Form.Group controlId="add-bookmark-modal-category">
                            <CategorySelect
                                categories={availableCategories}
                                name="categoryName"
                                value={categoryName}
                                onChange={(val) => setCategoryName(val)}
                                countType={CountType.Bookmarks}
                            />
                        </Form.Group>
                    </Stack>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button variant="primary" type="submit" disabled={!url.trim() || submitting}>
                        {submitting ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                Adding...
                            </>
                        ) : (
                            'Add bookmark'
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};
